import type React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import type { MotionValue, Variants } from 'motion/react';
import type { ColorName } from 'styles/config/colors';
import sizes from 'styles/config/sizes';

import useFixedOffset from '@polyai/common/hooks/useFixedOffset';
import useHideAfterClose from '@polyai/common/hooks/useHideAfterClose';
import useLockScroll from '@polyai/common/hooks/useLockScroll';
import useIsMoreScroll from '@polyai/common/lib/useIsMoreScroll';
import { DURATION } from '@polyai/common/utils/animationDuration';
import ConditionalWrapper from '@polyai/common/utils/conditionalWrapper';

import * as Styled from './StickySidebar.styled';

export {
  SidebarScrollableContent,
  SidebarStickyFooter,
  SidebarStickyHeader,
} from './StickySidebar.styled';

type Position = 'left' | 'right';

export type StickySidebarProps = {
  children:
    | React.ReactNode
    | ((values: {
        isScrolling: boolean;
        scrollableRef: React.MutableRefObject<HTMLDivElement | null>;
        isMoreScrollTop?: number;
        isMoreScrollBottom?: number;
      }) => React.ReactNode);
  id: string;
  isMainNavOpen: boolean;
  actInline?: boolean;
  overlayFullScreen?: boolean;
  open?: boolean;
  position?: Position;
  width?: number | MotionValue<number>;
  offset?: number;
  className?: string;
  stickyHeader?: boolean;
  zIndex?: number;
  headerOffset?: number;
  fullWidth?: boolean;
  backgroundColor?: ColorName;
  scrollIndicatorHeight?: number;
  variantsOverride?: Variants;
  'data-test-id'?: string;
  handleOwnAnimation?: boolean;
  maxWidth?: number;
  subSidebar?: React.ReactNode;
};

const DEFAULT_WIDTH = sizes.gridSidebarWidth;

interface OverlayProps {
  open: boolean;
  excludeRightPanelWidth?: number;
  children: React.ReactNode;
}

const Overlay: React.FC<OverlayProps> = ({
  open,
  excludeRightPanelWidth,
  children,
}) => {
  useLockScroll(open);
  return (
    <Styled.Overlay
      $excludeRightPanelWidth={excludeRightPanelWidth}
      $open={open}
    >
      {children}
    </Styled.Overlay>
  );
};

const StickySidebar = ({
  children,
  id,
  isMainNavOpen,
  actInline = true,
  overlayFullScreen,
  open = true,
  position = 'left',
  width = DEFAULT_WIDTH,
  offset = 0,
  className,
  zIndex = 10,
  stickyHeader = false,
  headerOffset = sizes.headerHeight,
  fullWidth,
  backgroundColor = 'background',
  scrollIndicatorHeight,
  variantsOverride,
  handleOwnAnimation,
  maxWidth,
  subSidebar,
  ...props
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
}: StickySidebarProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollableRef = useRef<HTMLDivElement | null>(null);
  const top = useFixedOffset(
    !overlayFullScreen ? headerOffset : 0,
    stickyHeader,
  );
  const { isScrolling, isMoreScrollTop, isMoreScrollBottom } = useIsMoreScroll(
    scrollableRef ?? containerRef,
    { isDisabled: stickyHeader, height: scrollIndicatorHeight ?? 80 },
  );

  const getRelativePosition = useCallback(
    (offset: number) => {
      return {
        [position]: `${offset}px`,
      };
    },
    [position],
  );

  const variants: Variants = useMemo(
    () =>
      variantsOverride ?? {
        open: {
          ...getRelativePosition(offset),
          transition: {
            duration: DURATION.FAST,
            staggerChildren: 0.05,
          },
        },
        closed: {
          ...getRelativePosition(-width),
          transition: {
            duration: DURATION.FAST,
          },
        },
      },
    [getRelativePosition, offset, variantsOverride, width],
  );

  const fakeVariants = useMemo(
    () => ({
      open: {
        width: fullWidth ? '100%' : `${width}px`,
        transition: {
          duration: DURATION.FAST,
          staggerChildren: 0.05,
        },
      },
      closed: {
        width: '0px',
        transition: {
          duration: DURATION.FAST,
        },
      },
    }),
    [fullWidth, width],
  );

  const animate = useMemo(() => (open ? 'open' : 'closed'), [open]);

  const { onAnimationStart, onAnimationEnd } = useHideAfterClose(
    open,
    containerRef,
    handleOwnAnimation,
  );

  const navWidth = sizes.mainNavWidth;
  const isFullScreenSidebar = fullWidth && position === 'right';

  const computedWidth =
    typeof width === 'number'
      ? isFullScreenSidebar && isMainNavOpen
        ? window.innerWidth - navWidth
        : isFullScreenSidebar
          ? window.innerWidth
          : width
      : width;

  const computedLeft =
    isFullScreenSidebar && isMainNavOpen
      ? `${navWidth}px`
      : isFullScreenSidebar
        ? '0'
        : undefined;

  return (
    <ConditionalWrapper
      condition={!!overlayFullScreen}
      wrapper={children => {
        return createPortal(
          <Overlay
            excludeRightPanelWidth={typeof offset === 'number' ? offset : 0}
            open={open}
          >
            {children}
          </Overlay>,
          document.body,
        );
      }}
    >
      {actInline && !overlayFullScreen && (
        <Styled.FakeSidebar
          animate={animate}
          initial={false}
          variants={fakeVariants}
        />
      )}
      <Styled.Sidebar
        ref={containerRef}
        $backgroundColor={backgroundColor}
        $isMainNavOpen={!overlayFullScreen && isMainNavOpen}
        $maxWidth={maxWidth}
        $position={position}
        $top={!overlayFullScreen ? top : 0}
        animate={animate}
        className={className}
        id={id}
        initial={false}
        style={{
          width: !handleOwnAnimation ? computedWidth : undefined,
          left: computedLeft,
          zIndex,
        }}
        variants={variants}
        onAnimationComplete={onAnimationEnd}
        onAnimationStart={onAnimationStart}
        {...props}
      >
        {typeof children === 'function'
          ? children({
              isScrolling,
              scrollableRef,
              isMoreScrollTop,
              isMoreScrollBottom,
            })
          : children}
      </Styled.Sidebar>
      {subSidebar}
      {!!isMoreScrollTop && !scrollIndicatorHeight && (
        <Styled.Fader
          animate={animate}
          initial={false}
          position="top"
          style={{
            width,
            height: isMoreScrollTop,
            top: top,
          }}
          variants={variants}
        />
      )}
      {!!isMoreScrollBottom && !scrollIndicatorHeight && (
        <Styled.Fader
          animate={animate}
          initial={false}
          position="bottom"
          style={{ width, height: isMoreScrollBottom }}
          variants={variants}
        />
      )}
    </ConditionalWrapper>
  );
};

export default StickySidebar;
